// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpointValues, { BreakpointValue } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import merge from "lodash/merge";
import React, { FC, PropsWithChildren, useContext, useMemo } from "react";
import { css } from "styled-components";

export interface SizedWrapperProps extends ExpandStyles<SizedWrapperStyles> {
    heights?: BreakpointValue[];
    paddings?: BreakpointValue[];
    sides?: Array<"top" | "bottom" | "left" | "right">;
}

export interface SizedWrapperStyles {
    wrapper?: InjectableCss;
    content?: InjectableCss;
}

export const sizedWrapperImageStyles = () => {
    return css`
        max-height: inherit;

        * {
            max-height: inherit;
        }

        img {
            width: auto;
            height: auto;
            max-width: 100%;
        }
    `;
};

export const sizedWrapperStyles: SizedWrapperStyles = {
    content: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: 100%;
        `,
    },
};

const SizedWrapper: FC<PropsWithChildren<SizedWrapperProps>> = ({
    children,
    heights = [],
    paddings = [],
    sides = ["top", "bottom"],
    extendedStyles,
    overridedStyles,
}) => {
    const styles = useExpandProps(sizedWrapperStyles, extendedStyles, overridedStyles);
    const { isEditing } = useContext(ShellContext);

    const maxHeights = useMemo(() => {
        const count = merge([], heights, paddings).length;
        const result = [];
        let lastHeight;
        let lastPadding;

        for (let index = 0; index < count; index++) {
            let height;
            let padding;
            let _index = index;

            while (_index >= 0 && (!height || !padding)) {
                height = height || heights?.[_index];
                padding = padding || paddings?.[_index];
                _index--;
            }

            if (!height || (height === lastHeight && padding === lastPadding)) {
                result.push(null);
            } else {
                result.push(padding ? `calc(${height} - (${padding} * 2))` : height);
            }

            lastHeight = height;
            lastPadding = padding;
        }

        return result;
    }, [heights, paddings, sides]);

    const wrapperCss = useMemo(() => {
        return css`
            ${breakpointValues(heights, "height")}
            ${breakpointValues(
                paddings,
                sides.map(x => `padding-${x}`),
            )}
            ${isEditing ? "height: auto !important;" : null}
            ${styles.wrapper?.css}
        `;
    }, [styles, heights, paddings, sides]);

    const contentCss = useMemo(() => {
        return css`
            ${breakpointValues(maxHeights, "max-height")}
            ${isEditing ? "max-height: none !important;" : null}
            ${styles.content?.css}
        `;
    }, [styles, maxHeights]);

    return (
        <StyledWrapper {...styles.wrapper} css={wrapperCss}>
            <StyledWrapper {...styles.content} css={contentCss}>
                {children}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default SizedWrapper;
